/* eslint-disable no-console */
import React, { useEffect } from "react";
import { Button, Menu, MenuItem, Popover, Theme, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// eslint-disable-next-line import/no-extraneous-dependencies
import { IMappedLoginNavigation } from "@/models/login/login-navigation-mapper";
import { Session } from "next-auth";
// import { useAuth } from "@/providers/auth/AuthContext";

interface LoggedInNavItemProps {
  session: Session;
  mappedData: IMappedLoginNavigation;
  anchorEl: HTMLButtonElement | null;
  popoverType: "menu" | "login" | null;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>, type: "menu" | "login") => void;
  handleClose: () => void;
  logout: () => void;
  userGroups: string[];
  isScrolled?: boolean;
}
const LoggedInNavItem = ({
  session,
  mappedData,
  anchorEl,
  popoverType,
  handleClick,
  handleClose,
  logout,
  userGroups,
  isScrolled = false
}: LoggedInNavItemProps) => {

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const shouldOffsetPopover = isScrolled && !isMobile;
  // Store the original site origin for redirect purposes
   useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("originalSiteOrigin", window.location.href);
    }
  }, []);

  // Helper functions for checking roles
  const hasRole = (role: string) => userGroups.includes(role);
  const isPassenger = hasRole("Passenger");
  const isAgentOrProfessional = hasRole("Agent") || hasRole("TravelProfessional");

  return (
    <>
      <Button
        data-testid="user-menu-button"
        sx={{
          color: "var(--isagent-text-color)",
          textTransform: "none",
          backgroundColor: (() => {
            if (isMobile) return "transparent";
            return "var(--background-color)";
          })(),
          "&:hover": {
            backgroundColor: (() => {
              if (isMobile) return "transparent";
              return "var(--background-color)"; // Default hover color
            })()
          }
        }}
        aria-describedby="user-menu"
        variant="contained"
        disableElevation
        onClick={(event) => handleClick(event, "menu")}
      >
         {session.user?.name && session.user?.name.length > 0 && (
           <>
             {mappedData.loggedIn} {session.user?.name?.split(" ")[0]} <ExpandMoreIcon />
           </>
         )}
      </Button>
      <Popover
        id="user-menu"
        data-testid="user-menu-popover"
        open={popoverType === "menu"}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
          sx={{ marginTop: shouldOffsetPopover ? "5rem" : 0 }}
        transitionDuration={0}
      >
        <Menu
          anchorEl={anchorEl}
          open={popoverType === "menu"}
          onClose={handleClose}
          data-testid="user-menu"
          sx={{ marginTop: shouldOffsetPopover ? "5rem" : 0 }}
          transitionDuration={0}
        >
          {/* Logout Button */}
          <MenuItem data-testid="logout-button" onClick={logout}>
            {mappedData.logout}
          </MenuItem>

          {/* External Links - conditionally render based on user roles */}
          {isAgentOrProfessional && mappedData.professionalLink && (
            <MenuItem
              data-testid={`externalLink-${mappedData.professionalLink.title}`}
              component="a"
              href={mappedData.professionalLink.url.toString()}
              target={mappedData.professionalLink.openInNewTab ? "_blank" : "_self"}
              rel={mappedData.professionalLink.openInNewTab ? "noopener noreferrer" : ""}
              sx={{ textDecoration: "none", color: "inherit" }}
              onClick={handleClose}
            >
              {mappedData.professionalLink.title}
            </MenuItem>
          )}

          {isPassenger && mappedData.passengerLink && (
            <MenuItem
              data-testid={`externalLink-${mappedData.passengerLink.title}`}
              component="a"
              href={mappedData.passengerLink.url.toString()}
              target={mappedData.passengerLink.openInNewTab ? "_blank" : "_self"}
              rel={mappedData.passengerLink.openInNewTab ? "noopener noreferrer" : ""}
              sx={{ textDecoration: "none", color: "inherit" }}
              onClick={handleClose}
            >
              {mappedData.passengerLink.title}
            </MenuItem>)}
        </Menu>
      </Popover>
    </>
  );
};

export default LoggedInNavItem;